import React from 'react';
import { T, R, FONT } from '../v2/theme';

// ═══ 부가서비스 A형 — 칩 선택 + 선택 표 (공용 프레젠테이션) ═══
// ExtraServicePicker(신청 폼)와 RequestServicesEditor(요청 상세 편집)가 공유한다.
// 칩 줄 = 카탈로그 토글, 표 = 선택된 행(금액·메모 편집 + 제거). 서버 계약은 각 호출부가 그대로 관리 —
// 이 컴포넌트는 rows/onXxx 콜백으로만 통신하고 상태를 갖지 않는다.

var chipBase = {
  display: 'inline-flex', alignItems: 'center', gap: 4, height: 30, padding: '0 12px', borderRadius: R.pill,
  fontSize: FONT.body, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', boxSizing: 'border-box'
};

function Chip({ label, checked, dashed, onClick }) {
  var style = Object.assign({}, chipBase,
    checked
      ? { background: T.navy, border: '1px solid ' + T.navy, color: '#fff', fontWeight: 600 }
      : { background: '#fff', border: (dashed ? '1px dashed ' : '1px solid ') + T.line, color: T.body }
  );
  return (
    <button type="button" onClick={onClick} style={style}>
      {checked && <span aria-hidden="true">✓</span>}
      {label}
    </button>
  );
}

function gridCols(showFee, readOnly) {
  var cols = ['minmax(0,1.4fr)'];
  if (showFee) cols.push('minmax(64px,84px)');
  if (!readOnly) cols.push('minmax(0,1fr)');
  if (!readOnly) cols.push('20px');
  return cols.join(' ');
}

function rowStyle(showFee, readOnly, extra) {
  return Object.assign({ display: 'grid', gridTemplateColumns: gridCols(showFee, readOnly), gap: 8, alignItems: 'center', padding: '6px 10px' }, extra || {});
}

var headCell = { fontSize: 11, fontWeight: 600, color: T.muted };
var nameCell = { fontSize: FONT.body, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var reissueFeeNote = { fontSize: 10, lineHeight: 1.3, color: T.faint };
var feeInputStyle = { width: '100%', padding: '4px 8px', border: '1px solid ' + T.bd, borderRadius: 6, fontSize: FONT.body, textAlign: 'right', fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box', fontFamily: 'inherit' };
var memoInputStyle = { width: '100%', padding: '4px 8px', border: '1px solid ' + T.bd, borderRadius: 6, fontSize: FONT.body, boxSizing: 'border-box', fontFamily: 'inherit' };
var nameInputStyle = Object.assign({}, memoInputStyle);
var removeBtnStyle = { width: 20, height: 20, border: 'none', background: 'transparent', color: T.faint, fontSize: 15, lineHeight: 1, cursor: 'pointer', padding: 0 };

// chips: [{id,name,defaultFee?}] 카탈로그 전체. selectedIds: Set(serviceId) — 칩 체크 상태.
// extraChip: {label, onClick} — 점선 "직접 입력" 칩(에디터 전용).
// rows: [{key, name, fee, memo, editableName?, kind?}] 선택된 행. showFee/readOnly 에 따라 열 구성이 바뀐다.
// kind==='reissue' 행은 금액 칸이 항상 고정 문구로 잠긴다(등록증 재교부 — 대행사 단가 적용, 편집 불가).
function ExtraServiceChipTable({ chips, selectedIds, onToggleChip, extraChip, rows, showFee, readOnly, onFeeChange, onMemoChange, onNameChange, onRemove, emptyText }) {
  var sum = showFee ? rows.reduce(function (s, r) { return s + (parseInt(r.fee, 10) || 0); }, 0) : 0;
  return (
    <div>
      {chips && chips.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {chips.map(function (c) {
            return <Chip key={c.id} label={c.name} checked={selectedIds.has(c.id)} onClick={function () { onToggleChip(c); }} />;
          })}
          {extraChip && <Chip label={extraChip.label} dashed onClick={extraChip.onClick} />}
        </div>
      )}
      {rows.length === 0
        ? <div style={{ marginTop: 8, fontSize: FONT.body, color: T.faint }}>{emptyText || '칩을 눌러 부가서비스를 선택하세요'}</div>
        : (<div style={{ marginTop: 8, border: '1px solid ' + T.bd, borderRadius: 8, overflow: 'hidden' }}>
            <div style={rowStyle(showFee, readOnly, { background: T.head })}>
              <span style={headCell}>선택 {rows.length}건</span>
              {showFee && <span style={Object.assign({}, headCell, { textAlign: 'right' })}>금액(원)</span>}
              {!readOnly && <span style={headCell}>메모</span>}
              {!readOnly && <span style={headCell} />}
            </div>
            {rows.map(function (r, idx) {
              return (<div key={r.key != null ? r.key : idx} style={rowStyle(showFee, readOnly, { borderTop: '1px solid ' + T.bd })}>
                {r.editableName && !readOnly
                  ? <input value={r.name || ''} onChange={function (e) { onNameChange(idx, e.target.value); }} placeholder="이름" style={nameInputStyle} />
                  : <span style={nameCell}>{r.name}</span>}
                {showFee && (r.kind === 'reissue'
                  ? <span style={reissueFeeNote}>대행사 재교부 단가 적용</span>
                  : (readOnly
                      ? <span style={{ fontSize: FONT.body, color: T.dim, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{(parseInt(r.fee, 10) || 0).toLocaleString()}</span>
                      : <input type="number" value={r.fee === undefined || r.fee === null ? '' : r.fee} onChange={function (e) { onFeeChange(idx, e.target.value); }} placeholder="금액" style={feeInputStyle} />))}
                {!readOnly && <input value={r.memo || ''} onChange={function (e) { onMemoChange(idx, e.target.value); }} placeholder="메모" style={memoInputStyle} />}
                {!readOnly && <button type="button" aria-label="제거" onClick={function () { onRemove(idx); }} style={removeBtnStyle}>×</button>}
              </div>);
            })}
            {showFee && (<div style={{ padding: '8px 10px', textAlign: 'right', fontSize: FONT.body, fontWeight: 700, color: T.ink, borderTop: '1px solid ' + T.bd, background: T.head }}>
              합계 {sum.toLocaleString()}원
            </div>)}
          </div>)}
    </div>
  );
}

export default ExtraServiceChipTable;
